import React from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Checkbox, FormControlLabel, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import ConfigCard from './ConfigCard';

// Per-element rows shown when the "apply to whole site" checkbox is OFF.
// `hasBgToggle` controls whether the row gets the "background-stretches-to-
// viewport-edges" checkbox — page-content blocks (MERKEN, Verkennen) don't,
// since they're not horizontal bands.
const PER_ELEMENT_ROWS = [
    { key: 'languageBar',     i18nKey: 'cfg_widths_el_language_bar',  fallback: 'Language bar',                hasBgToggle: true,  defaultWidth: 2500 },
    { key: 'header',          i18nKey: 'cfg_widths_el_header',         fallback: 'Header',                       hasBgToggle: true,  defaultWidth: 2500 },
    { key: 'infoBannerBelow', i18nKey: 'cfg_widths_el_info_banner_below', fallback: 'Info banner (below menu)', hasBgToggle: true,  defaultWidth: 1200 },
    { key: 'infoBannerTop',   i18nKey: 'cfg_widths_el_info_banner_top',   fallback: 'Info banner (top)',         hasBgToggle: true,  defaultWidth: 0    },
    { key: 'brands',          i18nKey: 'cfg_widths_el_brands',         fallback: 'Brands page',                  hasBgToggle: false, defaultWidth: 1500 },
    { key: 'explore',         i18nKey: 'cfg_widths_el_explore',        fallback: 'Explore page',                 hasBgToggle: false, defaultWidth: 1500 },
    { key: 'recentlyViewed',  i18nKey: 'cfg_widths_el_recently_viewed', fallback: 'Recently viewed articles',    hasBgToggle: true,  defaultWidth: 1500 },
    { key: 'footer',          i18nKey: 'cfg_widths_el_footer',         fallback: 'Footer',                       hasBgToggle: true,  defaultWidth: 1500 },
];

/**
 * "Global width settings" card — centralises the max-content-width values
 * that otherwise live spread out across the per-section modals (Header/Logo,
 * Footer, Image gallery, …). Admins typically want to set them all at once;
 * this card is the canonical place to do that.
 *
 * value shape:
 *   { applyToWholeSite: bool,
 *     productPagesWidth: number,    // px
 *     perElement: {
 *       languageBar:     { width, bgFullWidth },
 *       header:          { width, bgFullWidth },
 *       infoBannerBelow: { width, bgFullWidth },
 *       infoBannerTop:   { width, bgFullWidth },
 *       brands:          { width },              // no bg toggle
 *       explore:         { width },              // no bg toggle
 *       recentlyViewed:  { width, bgFullWidth },
 *       footer:          { width, bgFullWidth },
 *     }
 *   }
 *
 * onChange(field, val):
 *   - 'applyToWholeSite'                           → top-level checkbox
 *   - 'productPagesWidth'                          → single number above
 *   - 'perElement.<key>.width'                     → per-row width
 *   - 'perElement.<key>.bgFullWidth'               → per-row bg-stretch toggle
 */
// Module scope (F11): none of these depend on props/state, but declared
// inside the component they were rebuilt each render and fed ~30 MUI
// nodes — emotion re-serialised the whole grid on every typed digit.
// Sizing — compact on small AND mid-sized screens (incl. 1920×1080),
// since the modal is capped at md (~900px) and the previous normal
// values felt cramped even on large desktops. Only on extra-large
// viewports (lg+) we relax slightly. Breakpoint object syntax means
// MUI evaluates the value per breakpoint via media queries.
const labelFontSx     = { fontSize: { xs: '0.78rem', lg: '0.85rem' } };
const captionLineSx   = { lineHeight: 1.45 };
const alertSx         = {
    py: { xs: 0.5, lg: 0.75 },
    px: { xs: 1.5, lg: 1.75 },
    borderLeft: '3px solid',
    borderLeftColor: 'info.main',
    backgroundColor: 'rgba(0,0,0,0.03)',
    // MUI Alert's flex layout doesn't add overflow controls itself; the
    // long FormControlLabel inside can shove the row past the alert's
    // right edge and trigger a scrollbar. Force the message body to
    // wrap and clip whatever still escapes.
    overflowX: 'hidden',
    '& .MuiAlert-message': {
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
    },
};
// Make sure the FormControlLabel + its label can shrink and wrap
// instead of pushing the parent row wider than its container.
const inlineFormCtrlSx = {
    alignItems: 'flex-start',
    mt: 0.25,
    width: '100%',
    m: 0,
    '& .MuiFormControlLabel-label': {
        flex: 1,
        minWidth: 0,
        wordBreak: 'break-word',
    },
};
const inputCellSx     = {
    '& .MuiInputBase-input': {
        py: 0.5,
        fontSize: { xs: '0.8rem', lg: '0.85rem' },
    },
};
const adornmentSx     = {
    '& .MuiTypography-root': { fontSize: '0.7rem' },
};
const checkboxSx      = { py: 0.25 };
const formCtrlSx      = { ml: -0.5, mr: 0 };
const gridSx          = {
    display: 'grid',
    // Label column has a reserved minimum width so all labels line up
    // and there's a consistent gap before the input. Input stays fixed
    // width. Checkbox column (1fr) fills the rest so the row always
    // spans the full container width.
    gridTemplateColumns: { xs: '180px 100px 1fr', lg: '200px 120px 1fr' },
    rowGap: 0.75,
    columnGap: 2,
    alignItems: 'center',
};

const GlobalWidthSettingsCard = ({ value = {}, onChange, theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const applyToAll        = !!value.applyToWholeSite;
    const productPagesWidth = value.productPagesWidth ?? 1500;
    const perElement        = value.perElement || {};

    const set = (field) => (val) => onChange && onChange(field, val);
    const setRow = (key, sub) => (val) => onChange && onChange(`perElement.${key}.${sub}`, val);



    return (
        <ConfigCard
            anchor="cfg-card-widths"
            title={tr('cfg_widths_title', 'Global width settings')}
            description={tr('cfg_widths_desc', 'Maximum content widths used across the storefront. Often set in one go because they affect the whole layout.')}
            theme={theme}
        >
            {/* Top info banner + master checkbox */}
            <Alert severity="info" icon={false} sx={alertSx}>
                <Typography
                    variant="body2"
                    sx={{
                        mb: 0.25,
                        ...labelFontSx,
                        ...captionLineSx,
                        // The clipping `overflow: hidden` on the parent
                        // .MuiAlert-message is needed to keep the long
                        // FormControlLabel below from triggering a
                        // scrollbar — but it would also clip this
                        // intro line if the browser couldn't find a
                        // break point. `overflowWrap: anywhere` lets
                        // the text break inside parens like
                        // "(Aanbevolen: 1200)" so nothing gets cut off.
                        overflowWrap: 'anywhere',
                    }}
                >
                    {tr('cfg_widths_alert_recommended', "Set the maximum width for product-listing pages (home, explore, brands). Recommended: 1200.")}
                </Typography>
                <FormControlLabel
                    control={
                        <Checkbox
                            size="small"
                            checked={applyToAll}
                            onChange={(e) => set('applyToWholeSite')(e.target.checked)}
                            sx={checkboxSx}
                        />
                    }
                    label={
                        <Typography variant="body2" sx={{ ...labelFontSx, lineHeight: 1.35 }}>
                            {tr('cfg_widths_apply_all', 'Apply this maximum width to the whole webshop (navigation, footer, banners, …)')}
                        </Typography>
                    }
                    sx={inlineFormCtrlSx}
                />
            </Alert>

            {/* Single product-page width input */}
            <Box>
                <TextField
                    type="number"
                    size="small"
                    value={productPagesWidth}
                    onChange={(e) => set('productPagesWidth')(Math.max(0, parseInt(e.target.value) || 0))}
                    InputProps={{ endAdornment: <InputAdornment position="end" sx={adornmentSx}>px</InputAdornment> }}
                    sx={{ width: { xs: 130, lg: 150 }, ...inputCellSx }}
                />
            </Box>

            {/* Per-element settings — only when not applying-to-all */}
            {!applyToAll && (
                <>
                    <Alert severity="info" icon={false} sx={alertSx}>
                        <Typography variant="body2" sx={{ ...labelFontSx, ...captionLineSx, overflowWrap: 'anywhere' }}>
                            {tr('cfg_widths_alert_per_element', "If you haven't applied the max width to the whole webshop, you can still set the maximum width for each main element individually below.")}
                        </Typography>
                    </Alert>

                    <Box sx={gridSx}>
                        {PER_ELEMENT_ROWS.map(({ key, i18nKey, fallback, hasBgToggle, defaultWidth }) => {
                            const row = perElement[key] || {};
                            return (
                                <React.Fragment key={key}>
                                    <Typography sx={labelFontSx}>{tr(i18nKey, fallback)}</Typography>
                                    <TextField
                                        type="number"
                                        size="small"
                                        value={row.width ?? defaultWidth}
                                        onChange={(e) => setRow(key, 'width')(Math.max(0, parseInt(e.target.value) || 0))}
                                        InputProps={{ endAdornment: <InputAdornment position="end" sx={adornmentSx}>px</InputAdornment> }}
                                        sx={inputCellSx}
                                    />
                                    {hasBgToggle ? (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={row.bgFullWidth !== false}
                                                    onChange={(e) => setRow(key, 'bgFullWidth')(e.target.checked)}
                                                    sx={checkboxSx}
                                                />
                                            }
                                            label={<Typography sx={labelFontSx}>{tr('cfg_widths_bg_full', 'Background stretches to full width')}</Typography>}
                                            sx={formCtrlSx}
                                        />
                                    ) : (
                                        // Empty placeholder so the grid columns line up across rows.
                                        <Box />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Box>
                </>
            )}
        </ConfigCard>
    );
};

GlobalWidthSettingsCard.propTypes = {
    value:    PropTypes.object,
    onChange: PropTypes.func,
    theme:    PropTypes.string,
    t:        PropTypes.func,
};

export default GlobalWidthSettingsCard;
